import { useOpeningHours } from '@/features/opening-hours/hooks/useOpeningHours';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { OpeningHours } from '@restaurant/api-client';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m ?? 0);
}

export function nextOpeningMinutes(
  hours: OpeningHours[],
  timezone: string,
  service: 'PICKUP' | 'DELIVERY',
  nowMs: number = Date.now(),
): number | null {
  const own = hours.filter((h) => h.service === service);
  const rows = own.length > 0 ? own : hours.filter((h) => h.service === 'BASE');
  const todayDow = DateTime.fromMillis(nowMs).setZone(timezone).weekday % 7;
  for (let i = 0; i < 7; i++) {
    const dow = (todayDow + i) % 7;
    const day = rows.find((h) => h.dayOfWeek === dow && h.isEnabled && h.shifts.length > 0);
    if (day) return Math.min(...day.shifts.map((s) => minutesOf(s.open)));
  }
  return null;
}

export function useOpeningMinutes(orderType: 'PICKUP' | 'DELIVERY'): number | null {
  const timezone = useRestaurantStore((s) => s.restaurantConfig?.timezone);
  const { data: openingHours } = useOpeningHours();
  return useMemo(
    () => (openingHours ? nextOpeningMinutes(openingHours, timezone ?? 'UTC', orderType) : null),
    [openingHours, timezone, orderType],
  );
}
